<template>
  <div
    v-show="productsCount !== 0"
    class="search-modal grid"
  >
    <algolia-stats-connector @nb-hits-change="resultsChange" />
    <div
      v-show="query"
      class="col xs6 l1-3 search-modal__group-wrapper"
    >
      <ais-index :index-name="algolia.indexCollections">
        <ais-configure :hits-per-page.camel="5" />

        <ais-hits
          :class-names="{
            'ais-Hits': 'search-modal__group',
          }"
        >
          <template #default="{ items }">
            <h3
              class="search-modal__group-label text-body-m-bold"
              v-text="$string('predictive_search.collections_label')"
            />

            <ul
              v-if="items.length"
              class="search-modal__results-list"
            >
              <li
                v-for="item of items"
                :key="item.objectID"
              >
                <a
                  :href="getUrl(item, 'collections')"
                  class="text-body-s-regular search-modal__result-link"
                  v-text="item.title"
                />
              </li>
            </ul>

            <p
              v-else
              class="text-body-s-regular"
              v-text="$string('predictive_search.general_no_results')"
            />
          </template>
        </ais-hits>
      </ais-index>

      <ais-index :index-name="algolia.indexPages">
        <ais-configure :hits-per-page.camel="5" />

        <ais-hits
          :class-names="{
            'ais-Hits': 'search-modal__group',
          }"
        >
          <template #default="{ items }">
            <p
              class="text-body-m-bold"
              v-text="$string('predictive_search.pages_label')"
            />

            <ul
              v-if="items.length"
              class="search-modal__results-list"
            >
              <li
                v-for="item of items"
                :key="item.objectID"
              >
                <a
                  :href="getUrl(item, 'pages')"
                  class="text-body-s-regular search-modal__result-link"
                  v-text="item.title"
                />
              </li>
            </ul>
            <p
              v-else
              class="text-body-s-regular"
              v-text="$string('predictive_search.general_no_results')"
            />
          </template>
        </ais-hits>
      </ais-index>
    </div>

    <div
      v-show="!query"
      class="col xs6 l1-3 search-modal__group-wrapper"
    >
      <ais-index :index-name="algolia.indexSuggestions">
        <ais-configure :hits-per-page.camel="5" />

        <ais-hits
          :class-names="{
            'ais-Hits': 'search-modal__group',
          }"
        >
          <template #default="{ items }">
            <p
              class="text-body-m-bold"
              v-text="$string('predictive_search.suggestions_label')"
            />

            <ul
              v-if="items.length"
              class="search-modal__results-list"
            >
              <li
                v-for="item of items"
                :key="item.objectID"
              >
                <a
                  :href="getUrl(item, 'search')"
                  class="text-body-s-regular search-modal__result-link"
                  v-text="item.query"
                />
              </li>
            </ul>
            <p
              v-else
              class="text-body-s-regular"
              v-text="$string('predictive_search.general_no_results')"
            />
          </template>
        </ais-hits>
      </ais-index>
    </div>

    <div class="col xs6 l4-12">
      <ais-index :index-name="algolia.indexProducts">
        <!-- algolia-stats-connector component scoped to algolia.indexProducts -->
        <algolia-stats-connector @nb-hits-change="nbHitsChange" />

        <ais-configure :hits-per-page.camel="4" />
        <ais-hits
          :class-names="{
            'ais-Hits': 'search-modal__group',
          }"
          :transform-items="transformItems"
        >
          <template #default="{ items }">
            <div class="search-modal__group-header">
              <p
                v-if="query"
                class="text-body-m-bold"
                v-text="$string('predictive_search.products_label')"
              />
              <p
                v-else
                class="text-body-m-bold"
                v-text="$string('predictive_search.featured_products_label')"
              />

              <btn
                v-if="query"
                :label="$string('predictive_search.view_results')"
                class-label="search-modal__group-cta text-body-s-underline"
                :url="getUrl({query}, 'search')"
                modifiers="text"
              />
            </div>

            <ul class="search-modal__products">
              <li
                v-for="item of items"
                :key="item.objectID"
                class="search-modal__product"
              >
                <product-card
                  :product="item"
                  :position="item['__position']"
                  section-type="search modal"
                  section-name="search modal"
                />
              </li>
            </ul>
          </template>
        </ais-hits>
      </ais-index>
    </div>
  </div>

  <empty-search-state v-show="productsCount === 0" />
</template>

<script>

/**
 * Vue: Search overlay (search-modal)
 * -----------------------------------------------------------------------------
 * Predictive search overlay.
 * @param {Object} overlay - Overlay API.
 *
 */
import { AisConfigure, AisHits, AisIndex } from 'vue-instantsearch/vue3/es'
import Btn from '~global/btn/btn'
import ProductCard from '~global/product-card/product-card'
import AlgoliaStatsConnector from '~async/algolia/connectors/algolia-stats-connector'
import EmptySearchState from '~async/algolia/empty-search-state/empty-search-state'

import { formatAlgoliaProduct } from '~/format/format-algolia'

export default {
  name: 'SearchModal',

  components: {
    AisIndex,
    AisHits,
    AisConfigure,
    AlgoliaStatsConnector,
    Btn,
    ProductCard,
    EmptySearchState,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    query: {
      type: String,
      default: '',
    },
    searchRef: {
      type: [null, Object],
      default: null,
    },
  },

  data() {
    return {
      productsCount: -1,
      algolia: {
        indexProducts: this.$variable('algolia.indices.products'),
        indexCollections: this.$variable('algolia.indices.collections'),
        indexPages: this.$variable('algolia.indices.pages'),
        indexSuggestions: this.$variable('algolia.indices.suggestions'),
      },
    }
  },

  watch: {
    async 'overlay.isActive'() {
      if (this.overlay.isActive && this.searchRef) {
        await this.$nextTick()
        this.overlay.update({
          focusElements: {
            prepend: true,
            elements: [this.searchRef],
          },
        })
      }
    },

  },

  methods: {

    /**
     * Transform items to properly pass props to the product card component.
     * @param {Array} items - Algolia items.
     * @returns {Array}
     */
    transformItems(items) {
      const data = items.map((item) => formatAlgoliaProduct(item))

      return data
    },

    /**
     * Handler for number of hits change event.
     * Used to flag that algolia search box widget is rendered.
     */
    nbHitsChange(nbHits) {
      this.productsCount = nbHits
    },

    /**
     * Update focus trap on results change.
     */
    async resultsChange() {
      if (!this.overlay.isActive) {
        return
      }

      await this.$nextTick()

      this.overlay.update({
        focusElements: {
          prepend: true,
          elements: [this.searchRef],
        },
      })
    },

    /**
     * Get url for search page including Root route.
     */
    getUrl(record, type) {
      switch (type) {
        case 'search':
          return `${this.$variable('routes.search')}?query=${record.query}`
        case 'collections':
          return `${this.$variable('routes.root')}collections/${record.handle}`
        case 'pages':
          return `${this.$variable('routes.root')}pages/${record.handle}`
        default:
          return ''
      }
    },
  },
}
</script>

<style lang="scss">
@import './search-modal';
</style>
